import { useEffectEvent, useLayoutEffect, useState } from 'react';

import { useThrottledCallback } from './useThrottledCallback';

type SavedScrollPosition = {
  top: number;
  left: number;
  savedAt: number;
};

type Options = {
  key: string;
  maxAgeMs: number;
  enabled?: boolean;
};

const STORAGE_KEY_PREFIX = 'scroll-position:';
const SAVE_THROTTLE_MS = 200;

const isSavedScrollPosition = (
  value: unknown
): value is SavedScrollPosition => {
  if (typeof value !== 'object' || value === null) return false;

  const { top, left, savedAt } = value as Partial<
    Record<keyof SavedScrollPosition, unknown>
  >;

  return (
    typeof top === 'number' &&
    typeof left === 'number' &&
    typeof savedAt === 'number'
  );
};

const readPosition = (
  key: string,
  maxAgeMs: number
): SavedScrollPosition | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_PREFIX + key);

    if (!raw) return null;

    const saved: unknown = JSON.parse(raw);

    if (!isSavedScrollPosition(saved)) return null;
    if (Date.now() - saved.savedAt > maxAgeMs) {
      sessionStorage.removeItem(STORAGE_KEY_PREFIX + key);
      return null;
    }
    return saved;
  } catch {
    return null;
  }
};

const writePosition = (key: string, position: SavedScrollPosition) => {
  try {
    sessionStorage.setItem(STORAGE_KEY_PREFIX + key, JSON.stringify(position));
  } catch {
    // Storage can be full or unavailable (e.g. Safari private browsing)
  }
};

/**
 * Saves an element's scroll position as it scrolls, and restores it before
 * paint when the element mounts again, so returning to a page puts the user
 * back where they were without a visible jump.
 *
 * Returns a callback ref, so a replacement element (e.g. one that unmounts
 * and remounts inside an animation) is picked up and restored too.
 */
export const usePersistedScrollPosition = (options: Options) => {
  const { key, maxAgeMs, enabled = true } = options;

  const [element, setElement] = useState<HTMLElement | null>(null);

  const save = useThrottledCallback(writePosition, SAVE_THROTTLE_MS);

  const onScroll = useEffectEvent((el: HTMLElement) => {
    save(key, { top: el.scrollTop, left: el.scrollLeft, savedAt: Date.now() });
  });

  const restore = useEffectEvent((el: HTMLElement) => {
    const saved = readPosition(key, maxAgeMs);
    if (!saved) return;

    el.scrollTop = saved.top;
    el.scrollLeft = saved.left;
  });

  useLayoutEffect(() => {
    if (enabled && element) restore(element);
  }, [element, key, enabled]);

  useLayoutEffect(() => {
    if (!enabled || !element) return undefined;

    const handleScroll = () => onScroll(element);
    element.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      element.removeEventListener('scroll', handleScroll);
      save.flush();
    };
  }, [element, enabled, save]);

  return setElement;
};
